import { useEffect, useState } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { GEOREFERENCED_MAPS_ANCHOR_ID } from '~/components2/Map/Widgets/HighlightLayer';
import { useGeoreferencedMaps } from '~/hooks/useGeoreferencedMaps';
import layerStore from '~/store2/layers';
import {
  GEOREFERENCED_MAP_LAYER_PREFIX,
  georeferencedMapLayerId,
  georeferencedMapRenderId,
  georeferencedMapsToTocItems,
  visibleMapsInDrawOrder,
} from './georeferencedMapLayers.helpers';
import georeferencedMapsStore from './georeferencedMapsStore';

function visibleGeoreferencedMapLayerIds(): string[] {
  return [...layerStore.visibleLayerIds].filter((id) => id.startsWith(GEOREFERENCED_MAP_LAYER_PREFIX));
}

/**
 * Draws each switched-on geo-referenced map as one PNG placed by its four corners. Mounted in
 * Surface so maps stay on across tabs; renders nothing while `visualize-georeferenced-maps` is off.
 */
export default function GeoreferencedMapLayers({ projectId }: { projectId: string }) {
  const { data: maps } = useGeoreferencedMaps(projectId);
  const { visibleLayerIds } = useSnapshot(layerStore) as typeof layerStore;
  const { opacity } = useSnapshot(georeferencedMapsStore);
  const { current: map } = MapGL.useMap();
  // `beforeId` only resolves once the anchor exists; without it Mapbox draws the PNGs on top of everything.
  const [isAnchorLoaded, setAnchorLoaded] = useState(() => !!map?.getLayer(GEOREFERENCED_MAPS_ANCHOR_ID));

  // Registers the Geo-referenced Maps panel group whenever the set of maps changes; keyed on layer ids, not signed URLs.
  const mapIdsKey = (maps ?? []).map(georeferencedMapLayerId).join(',');
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the layer ids above
  useEffect(() => {
    layerStore.setGeoreferencedMapsTOCGroup(georeferencedMapsToTocItems(maps));
  }, [mapIdsKey]);

  // Drop this project's group and switched-on maps so they don't carry over to the next project.
  useEffect(() => {
    return () => {
      for (const id of visibleGeoreferencedMapLayerIds()) layerStore.visibleLayerIds.delete(id);
      layerStore.setGeoreferencedMapsTOCGroup([]);
    };
  }, []);

  useEffect(() => {
    if (!map) return;
    const checkAnchor = () => setAnchorLoaded(!!map.getLayer(GEOREFERENCED_MAPS_ANCHOR_ID));
    checkAnchor();
    map.on('styledata', checkAnchor);
    return () => {
      map.off('styledata', checkAnchor);
    };
  }, [map]);

  if (!isAnchorLoaded) return null;

  return (
    <>
      {visibleMapsInDrawOrder(maps, visibleLayerIds).map((georeferencedMap) => {
        const layerId = georeferencedMapRenderId(georeferencedMap.id);
        return (
          <MapGL.Source
            key={layerId}
            id={layerId}
            type="image"
            url={georeferencedMap.pngUrl}
            coordinates={georeferencedMap.corners}
          >
            <MapGL.Layer
              id={layerId}
              source={layerId}
              type="raster"
              minzoom={12}
              paint={{ 'raster-opacity': opacity, 'raster-fade-duration': 0, 'raster-emissive-strength': 1 }}
              beforeId={GEOREFERENCED_MAPS_ANCHOR_ID}
            />
          </MapGL.Source>
        );
      })}
    </>
  );
}
